import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, User, Eye, EyeOff, Sun, Moon } from 'lucide-react';

/** Valeur de loginError pour le message « compte désactivé » (affichage dédié sur deux lignes) */
const LOGIN_ERR_DESACTIVE = 'desactive';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await login(username.trim(), password);
      toast.success('Connexion réussie!');
      navigate('/');
    } catch (error) {
      const status = error.response?.status;
      const rawDetail = error.response?.data?.detail;

      const isMaintenance =
        status === 503 ||
        status === 520 ||
        (rawDetail &&
          typeof rawDetail === 'object' &&
          !Array.isArray(rawDetail) &&
          rawDetail.message === 'Application en maintenance');

      let detailText = rawDetail;
      if (typeof rawDetail === 'object' && rawDetail !== null && !Array.isArray(rawDetail) && rawDetail.message) {
        detailText = rawDetail.message;
      } else if (Array.isArray(rawDetail) && rawDetail.length > 0) {
        detailText = typeof rawDetail[0] === 'string' ? rawDetail[0] : rawDetail[0]?.msg;
      }

      if (isMaintenance) {
        navigate('/maintenance');
      } else if (status === 403 && detailText === 'Compte désactivé') {
        setLoginError(LOGIN_ERR_DESACTIVE);
      } else {
        toast.error('Identifiants incorrects');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f76f4b] to-[#f4af14] dark:from-gray-900 dark:to-gray-800 relative">
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-3 rounded-full bg-white/20 dark:bg-gray-800/50 hover:bg-white/30 dark:hover:bg-gray-700/50 backdrop-blur-sm transition-all"
        data-testid="theme-toggle"
        title={theme === 'light' ? 'Mode sombre' : 'Mode clair'}
      >
        {theme === 'light' ? (
          <Moon size={20} className="text-white" />
        ) : (
          <Sun size={20} className="text-yellow-300" />
        )}
      </button>

      <div className="w-full max-w-md px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800 }}>Prysm&apos;App</h1>
          <p className="text-white text-lg" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>Plateforme unifiée du Groupe Prysme</p>
        </div>

        <Card className="backdrop-blur-md bg-white/95 dark:bg-gray-800/95 border-0 shadow-2xl" data-testid="login-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center text-[#080808] dark:text-white">Connexion</CardTitle>
            <CardDescription className="text-center dark:text-gray-300">
              Accédez à toutes vos applications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {loginError === LOGIN_ERR_DESACTIVE ? (
                <div
                  role="alert"
                  className="space-y-2 rounded-lg border border-red-500/70 bg-red-500/10 px-3 py-3 text-center text-sm font-medium text-red-800 dark:border-red-400/60 dark:bg-red-950/50 dark:text-red-100"
                  data-testid="login-deactivated-message"
                >
                  <p className="leading-snug">Votre compte a été désactivé.</p>
                  <p className="leading-snug">
                    Merci de contacter le support pour faire réactiver votre compte.
                  </p>
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#080808] dark:text-gray-200 font-medium">Identifiant</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    type="text"
                    placeholder="prenom.nom"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setLoginError('');
                    }}
                    className="pl-10 border-gray-300 focus:border-[#f76f4b] focus:ring-[#f76f4b]"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#080808] dark:text-gray-200 font-medium">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setLoginError('');
                    }}
                    className="pl-10 pr-10 border-gray-300 focus:border-[#f76f4b] focus:ring-[#f76f4b]"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full bg-[#f76f4b] hover:bg-[#e55d3b] text-white font-semibold py-6 rounded-xl shadow-lg"
                disabled={loading}
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
